// src/pages/AprovarChecklists.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/pages/aprovarChecklists.scss";

/* ================= Helpers ================ */
const fmtDT = (v) => (v ? new Date(v).toLocaleString("pt-BR", { hour12: false }) : "—");
const friendlyErr = (e, fallback = "Falha na operação.") => {
  const msg = e?.response?.data?.mensagem || e?.response?.data?.error || e?.message;
  const st = e?.response?.status;
  return st ? `${fallback} (HTTP ${st})${msg ? ` — ${msg}` : ""}` : msg || fallback;
};

// tenta achar o id numérico do funcionário logado
const getLoggedUserId = () => {
  const cands = [
    sessionStorage.getItem("codFunc"),
    sessionStorage.getItem("CodCadFunc"),
    sessionStorage.getItem("id"),
  ];
  for (const c of cands) {
    const n = Number(c);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
};

// tenta achar o login de usuário (quando existir)
const getUsuarioLogin = () => {
  const ss = (k) => (sessionStorage.getItem(k) || localStorage.getItem(k) || "").trim();
  const rawUser = ss("user");
  try {
    const j = rawUser ? JSON.parse(rawUser) : null;
    if (j?.usuario) return String(j.usuario).trim();
    if (j?.login) return String(j.login).trim();
  } catch {}
  return ss("USUARIO") || ss("usuario") || "";
};
const getCodFunc = () => {
  const v = Number(sessionStorage.getItem("codFunc") || localStorage.getItem("codFunc") || 0);
  return Number.isFinite(v) && v > 0 ? v : null;
};

/* ================= API ================ */
// lista itens para aprovação
async function apiListarAprovacoes({ status, cliente_id, template_id, data_de, data_ate, limit, offset }) {
  const { data } = await api.get("/validacao/aprovacoes", {
    params: { status, cliente_id, template_id, data_de, data_ate, limit, offset },
  });
  return Array.isArray(data) ? data : [];
}
// detalhe completo da execução (inclui perguntas, respostas, assinatura, restrições)
async function apiObterExecucao(execId) {
  const { data } = await api.get(`/validacao/execucoes/${execId}`);
  return data;
}
// patch de decisão
async function apiDecidir(execId, body) {
  const { data } = await api.patch(`/validacao/execucoes/${execId}/aprovacao`, body);
  return data; // {sucesso, execucao_id, cliente_id, novo_status, restricoes_inseridas, finalize_sugerido}
}
// finalizar no SSW
async function apiFinalizarChecklistSSW({ idCliente, usuario, codFunc, observacoes }) {
  const { data } = await api.post("/ssw/checklist/finalizar", {
    idCliente,
    usuario: usuario || null,
    codFunc: codFunc ?? null,
    observacoes: observacoes || "",
  });
  return data;
}
// pegar cliente por codigoCad para exibir razão/fantasia
async function apiBuscarClientePorCodigo(codigoCad) {
  if (!codigoCad) return null;
  try {
    const { data } = await api.get("/cliente/por-codigo", { params: { codigo: codigoCad } });
    return data || null;
  } catch {
    return null;
  }
}

/* ================= Página ================ */
export default function AprovarChecklists() {
  const [tab, setTab] = useState("Pendentes");
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const decidirBusyRef = useRef(false);
  const [decidirBusy, setDecidirBusy] = useState(false);

  // filtros simples (podemos expandir depois)
  const [clienteId, setClienteId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [rangeDe, setRangeDe] = useState("");
  const [rangeAte, setRangeAte] = useState("");

  // cache de clientes (codigoCad -> {RazaoSocial, NomeFant})
  const [clientesMap, setClientesMap] = useState({});

  // painel de detalhe
  const [openId, setOpenId] = useState(null);
  const [det, setDet] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  // decisão
  const [observacao, setObservacao] = useState("");
  const [restricoes, setRestricoes] = useState([""]);
  const aprovadorUserId = getLoggedUserId();
  const usuarioLogin = getUsuarioLogin();
  const codFuncAtual = getCodFunc();

  /* --------- carregar lista --------- */
  async function reload() {
    setLoading(true);
    try {
      const items = await apiListarAprovacoes({
        status: tab,
        cliente_id: clienteId || undefined,
        template_id: templateId || undefined,
        data_de: rangeDe || undefined,
        data_ate: rangeAte || undefined,
        limit: 50,
        offset: 0,
      });
      setLista(items);

      // busca nomes dos clientes que ainda não temos no cache
      const uniq = [...new Set(items.map((x) => x.ClienteId).filter(Boolean))];
      const misses = uniq.filter((c) => !clientesMap[c]);
      if (misses.length) {
        const results = await Promise.all(misses.map(apiBuscarClientePorCodigo));
        const patch = {};
        results.forEach((cli, idx) => {
          const key = misses[idx];
          if (cli) patch[key] = { RazaoSocial: cli.RazaoSocial || "", NomeFant: cli.NomeFant || "" };
          else patch[key] = { RazaoSocial: "", NomeFant: "" };
        });
        setClientesMap((prev) => ({ ...prev, ...patch }));
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha ao carregar checklists."));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reload(); // eslint-disable-next-line
  }, [tab]);

  /* --------- abrir detalhe --------- */
  async function abrirDetalhe(execId) {
    setOpenId(execId);
    setDet(null);
    setObservacao("");
    setRestricoes([""]);
    setDetLoading(true);
    try {
      const data = await apiObterExecucao(execId);
      setDet(data);

      // garante cache do cliente do detalhe
      const cod = data?.execucao?.ClienteId;
      if (cod && !clientesMap[cod]) {
        const cli = await apiBuscarClientePorCodigo(cod);
        if (cli)
          setClientesMap((prev) => ({
            ...prev,
            [cod]: { RazaoSocial: cli.RazaoSocial || "", NomeFant: cli.NomeFant || "" },
          }));
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha ao carregar detalhes da execução."));
    } finally {
      setDetLoading(false);
    }
  }

  /* --------- ações de decisão --------- */
  async function decidir(acao) {
    if (!openId) return;

    if (decidirBusyRef.current) return;
    decidirBusyRef.current = true;
    setDecidirBusy(true);

    try {
      const body = {
        acao,
        observacao: observacao || null,
        aprovador_user_id: aprovadorUserId ?? null,
      };
      if (acao === "APROVAR_RESTRICAO") {
        const list = (restricoes || []).map((s) => String(s || "").trim()).filter(Boolean);
        if (list.length === 0) {
          showError("Informe ao menos uma restrição.");
          decidirBusyRef.current = false;
          setDecidirBusy(false);
          return;
        }
        body.restricoes = list;
      }

      const r = await apiDecidir(openId, body);

      const msg =
        acao === "APROVAR"
          ? "Checklist aprovado."
          : acao === "REPROVAR"
          ? "Checklist reprovado."
          : "Checklist aprovado com restrição.";
      showInfo(msg);

      // ===== FINALIZAÇÃO NO SSW (sem chamada extra) =====
      const isAprovado = acao === "APROVAR" || acao === "APROVAR_RESTRICAO";
      const clienteIdDaExec = r?.cliente_id ?? det?.execucao?.ClienteId ?? null;

      if (isAprovado && clienteIdDaExec) {
        if (r?.finalize_sugerido) {
          // sem pendências → tenta finalizar no SSW (manda usuario e codFunc se disponíveis)
          try {
            const obsAuto =
              `[auto] Finalização por aprovação de todos os checklists (execução #${openId}).` +
              (observacao ? ` Observação do aprovador: ${observacao}` : "");
            await apiFinalizarChecklistSSW({
              idCliente: clienteIdDaExec,
              usuario: usuarioLogin || null,
              codFunc: codFuncAtual ?? null,
              observacoes: obsAuto,
            });
            showInfo("Cliente marcado como 'Finalizado' no SSW.");
          } catch (finErr) {
            const detalhe =
              finErr?.response?.data?.error ||
              finErr?.response?.data?.mensagem ||
              finErr?.message ||
              "";
            showInfo(
              "Checklist aprovado ✅. Tentamos finalizar o cliente no SSW, mas não foi possível agora. " +
                "Você pode tentar finalizar depois pela UI." +
                (detalhe ? ` Detalhe: ${detalhe}` : "")
            );
          }
        } else {
          // ainda existem pendentes para o cliente
          showInfo(
            "Checklist aprovado ✅. Existem outros checklists pendentes para este cliente — finalize quando todos estiverem aprovados."
          );
        }
      }

      // fecha o detalhe e recarrega a lista
      setOpenId(null);
      setDet(null);
      reload();
    } catch (e) {
      showError(friendlyErr(e, "Não foi possível registrar a decisão."));
    } finally {
      decidirBusyRef.current = false;
      setDecidirBusy(false);
    }
  }

  /* --------- helpers UI --------- */
  const pendentes = useMemo(
    () => lista.filter((x) => (x.AprovacaoStatus || "PENDENTE") === "PENDENTE").length,
    [lista]
  );

  return (
    <MainLayout>
      <div className="ssw-page-approve">
        {/* Header */}
        <div className="ap-header">
          <h1>Aprovação de Checklists</h1>
          <div className="ap-tabs">
            {["Pendentes", "Analisados", "Todos"].map((t) => (
              <button
                key={t}
                className={`ap-tab ${tab === t ? "is-active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
                {t === "Pendentes" && pendentes ? ` (${pendentes})` : ""}
              </button>
            ))}
          </div>
          <div className="ap-actions">
            <button className="ssw-btn ssw-btn--ghost" onClick={reload} disabled={loading}>
              {loading ? "Atualizando…" : "Atualizar"}
            </button>
          </div>
        </div>

        {/* Filtros rápidos */}
        <div className="ap-card">
          <div className="ap-grid ap-grid--4">
            <div className="ap-field">
              <label>Cliente (Cód. Cad.)</label>
              <input className="ssw-input" value={clienteId} onChange={(e) => setClienteId(e.target.value)} />
            </div>
            <div className="ap-field">
              <label>Template Id</label>
              <input className="ssw-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} />
            </div>
            <div className="ap-field">
              <label>Concluído de</label>
              <input type="date" className="ssw-input" value={rangeDe} onChange={(e) => setRangeDe(e.target.value)} />
            </div>
            <div className="ap-field">
              <label>Concluído até</label>
              <input type="date" className="ssw-input" value={rangeAte} onChange={(e) => setRangeAte(e.target.value)} />
            </div>
          </div>
          <div className="ap-right">
            <button className="ssw-btn" onClick={reload} disabled={loading}>
              Filtrar
            </button>
          </div>
        </div>

        {/* Lista */}
        <div className="ap-card">
          <div className="ap-tablewrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th>Cliente</th>
                  <th>Fim</th>
                  <th>Status aprovação</th>
                  <th>Restrições</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lista.length === 0 && (
                  <tr>
                    <td colSpan={7} className="ap-empty">
                      {loading ? "Carregando…" : "Nenhum item."}
                    </td>
                  </tr>
                )}
                {lista.map((it) => {
                  const cli = clientesMap[it.ClienteId] || {};
                  const nomeCliente = cli.RazaoSocial || cli.NomeFant
                    ? `${cli.RazaoSocial || "—"}${cli.NomeFant ? ` / ${cli.NomeFant}` : ""}`
                    : String(it.ClienteId || "—");
                  const st = (it.AprovacaoStatus || "PENDENTE").toUpperCase(); // PENDENTE | APROVADO | APROV_RESTRICAO | REPROVADO
                  return (
                    <tr key={it.Id}>
                      <td>#{it.Id}</td>
                      <td>{it.Titulo}</td>
                      <td title={`Cod: ${it.ClienteId}`}>{nomeCliente}</td>
                      <td>{fmtDT(it.DtFim)}</td>
                      <td>
                        <span className={`ap-badge st-${st.toLowerCase()}`}>{st}</span>
                      </td>
                      <td>{it.QtdeRestricoes || 0}</td>
                      <td className="ap-right">
                        <button className="ssw-btn ssw-btn--secondary" onClick={() => abrirDetalhe(it.Id)}>
                          {tab === "Pendentes" ? "Analisar" : "Ver"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Painel de detalhe / decisão */}
        {openId && (
          <div className="ap-drawer">
            <div className="ap-drawer__content">
              <div className="ap-drawer__head">
                <div>
                  <h3>Execução #{openId}</h3>
                  {det?.execucao && (
                    <div className="ap-sub">
                      <span className="ap-kpi">
                        Cliente:{" "}
                        {clientesMap[det.execucao.ClienteId]
                          ? `${clientesMap[det.execucao.ClienteId].RazaoSocial || "—"}${
                              clientesMap[det.execucao.ClienteId].NomeFant
                                ? ` / ${clientesMap[det.execucao.ClienteId].NomeFant}`
                                : ""
                            }`
                          : det.execucao.ClienteId}
                      </span>
                      <span className="ap-kpi">
                        Template: {det.execucao.TemplateId} (v{det.execucao.TemplateVersao})
                      </span>
                      <span className="ap-kpi">Concluído: {fmtDT(det.execucao.DtFim)}</span>
                    </div>
                  )}
                </div>
                <button
                  className="ap-close"
                  onClick={() => {
                    setOpenId(null);
                    setDet(null);
                    setObservacao("");
                    setRestricoes([""]);
                    decidirBusyRef.current = false;
                    setDecidirBusy(false);
                  }}
                >
                  ✕
                </button>
              </div>

              {detLoading && <div className="ap-loading">Carregando…</div>}

              {!detLoading && det && (
                <>
                  {/* Perguntas e respostas */}
                  <div className="ap-block">
                    <h4>Respostas</h4>
                    <div className="ap-tablewrap">
                      <table className="ap-table ap-table--compact">
                        <thead>
                          <tr>
                            <th className="w-8">#</th>
                            <th>Pergunta</th>
                            <th className="w-12">Obrigatória</th>
                            <th className="w-12">Resposta</th>
                            <th className="w-36">Comentário</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(det.perguntas ?? []).map((p) => {
                            const r = (det.respostas || []).find((x) => x.PerguntaId === p.Id);
                            const v = r?.RespostaBool;
                            return (
                              <tr key={p.Id}>
                                <td>{p.Ordem}</td>
                                <td>{p.Enunciado}</td>
                                <td>{p.Obrigatoria ? "Sim" : "Não"}</td>
                                <td>{v === null || v === undefined ? "—" : v ? "Sim" : "Não"}</td>
                                <td>{r?.Comentario || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Restrições existentes */}
                  <div className="ap-block">
                    <h4>Restrições existentes</h4>
                    {!det.restricoes || det.restricoes.length === 0 ? (
                      <div className="ap-empty">Sem restrições registradas.</div>
                    ) : (
                      <ul className="ap-restricoes">
                        {det.restricoes.map((r) => (
                          <li key={r.Id}>
                            <span className={`st st-${(r.Status || "ABERTA").toLowerCase()}`}>{r.Status}</span>
                            <span className="desc">{r.Descricao}</span>
                            <span className="when">{fmtDT(r.CriadaEm)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Decisão (se ainda pendente) */}
                  {(det.execucao?.AprovacaoStatus ?? "PENDENTE") === "PENDENTE" ? (
                    <div className="ap-block">
                      <h4>Decisão</h4>
                      <div className="ap-field">
                        <label>Observação</label>
                        <textarea
                          className="ssw-textarea"
                          placeholder="Observações da análise (opcional para aprovar; obrigatório para reprovar)"
                          value={observacao}
                          onChange={(e) => setObservacao(e.target.value)}
                        />
                      </div>

                      <div className="ap-field">
                        <label>Restrições (para Aprovar com Restrição)</label>
                        {restricoes.map((r, i) => (
                          <div key={i} className="ap-row">
                            <input
                              className="ssw-input"
                              placeholder={`Restrição ${i + 1}`}
                              value={r}
                              onChange={(e) => {
                                const clone = [...restricoes];
                                clone[i] = e.target.value;
                                setRestricoes(clone);
                              }}
                            />
                            <button
                              className="ssw-btn ssw-btn--ghost"
                              onClick={() => setRestricoes((prev) => prev.filter((_, j) => j !== i))}
                              disabled={restricoes.length === 1}
                            >
                              Remover
                            </button>
                          </div>
                        ))}
                        <div>
                          <button className="ssw-btn ssw-btn--secondary" onClick={() => setRestricoes((p) => [...p, ""])}>
                            + Adicionar restrição
                          </button>
                        </div>
                      </div>

                      <div className="ap-actions-row">
                        <button className="ssw-btn" onClick={() => decidir("APROVAR")} disabled={decidirBusy}>
                          Aprovar
                        </button>
                        <button
                          className="ssw-btn ssw-btn--secondary"
                          onClick={() => decidir("APROVAR_RESTRICAO")}
                          disabled={decidirBusy}
                        >
                          Aprovar com restrição
                        </button>
                        <button
                          className="ssw-btn ssw-btn--ghost"
                          onClick={() => {
                            if (!observacao.trim()) return showError("Para reprovar, preencha a Observação.");
                            decidir("REPROVAR");
                          }}
                          disabled={decidirBusy}
                        >
                          Reprovar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ap-block">
                      <h4>Decisão registrada</h4>
                      <div className="ap-pill">
                        Status: {det.execucao.AprovacaoStatus || "—"} • Em: {fmtDT(det.execucao.AprovadoEm)}
                      </div>
                      <div className="ap-field">
                        <label>Observação do aprovador</label>
                        <textarea className="ssw-textarea" value={det.execucao.AprovacaoObs || ""} readOnly />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
